import React, { useState } from 'react';
import { Box, Container, Typography, FormHelperText, FormControl, OutlinedInput, InputAdornment, IconButton, Button } from '@mui/material';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { logout } from '../../../Helpers/userSlice';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const AddUserLabel = ({ children }) => (
  <Typography
    variant='h2'
    sx={{ fontFamily: 'Lato, sans-serif', fontSize: '14px', fontWeight: '500', color: '#000000', mb: 1 }}
  >
    {children}
  </Typography>
);

export default function ChangePasswordForm() {
  const [formData, setFormData] = useState({ oldPassword: '', newPassword: '', confirmNewPassword: '' });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState({ oldPassword: false, newPassword: false, confirmNewPassword: false });

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleClickShowPassword = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { oldPassword, newPassword, confirmNewPassword } = formData;
    let newErrors = {};
    if (!oldPassword) newErrors.oldPassword = true;
    if (!newPassword) newErrors.newPassword = true;
    if (!confirmNewPassword) newErrors.confirmNewPassword = true;
    if (newPassword !== confirmNewPassword) newErrors.confirmNewPassword = 'Passwords do not match';

    const passwordRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,40}$/;

    if (newPassword && !passwordRegex.test(newPassword)) {
      toast.error("New password must include at least 1 uppercase, 1 lowercase, 1 number, and 1 special character.");
      return;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please correct the errors.');
      return;
    }

    try {
      const accessToken = localStorage.getItem('NCProAccessToken');
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/user/change-password`,
        { oldPassword, newPassword, confirmNewPassword },
        { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` } }
      );
      toast.success(response.data?.message || 'Password changed successfully');
      localStorage.removeItem('NCProAccessToken');
      localStorage.removeItem('NCProRefreshToken');
      dispatch(logout());
      navigate('/login');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.message || 'Something went wrong');
    }
  };

  return (
    <>
      <Container sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '35%', minHeight: '80vh', pt: 8, pb: 2, px: 5 }}>
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ pr: '4px' }}>
            <img
              src="/images/leftArrow.png"
              alt="Icon"
              style={{ width: 24, height: 24, marginRight: 4, cursor: 'pointer' }}
              onClick={() => navigate('/ncpro/welcome')}
            />
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: 'Lato',
                fontSize: '18px',
                fontWeight: 700,
              }}
            >
              {formData.ncr_id} Back
            </Typography>
          </Box>
        </Box>
        <Box sx={{ pt: 2 }}>
          <Typography variant='h1' sx={{ fontFamily: 'Lato, sans-serif', fontSize: '26px', fontWeight: '900', color: '#000', mb: 4 }}>
            Change Password
          </Typography>
        </Box>
        <form onSubmit={handleSubmit}>
          {['oldPassword', 'newPassword', 'confirmNewPassword'].map((field) => (
            <Box key={field} sx={{ mb: 2 }}>
              <AddUserLabel>{field === 'oldPassword' ? 'Old Password' : field === 'newPassword' ? 'New Password' : 'Confirm Password'}</AddUserLabel>
              <FormControl fullWidth error={errors[field]} size='small'>
                <OutlinedInput
                  name={field}
                  size='medium'
                  type={showPassword[field] ? 'text' : 'password'}
                  value={formData[field]}
                  onChange={handleChange}
                  placeholder={`Enter your ${field === 'oldPassword' ? 'old' : field === 'newPassword' ? 'new' : 'confirmed new'} password`}
                  endAdornment={
                    <InputAdornment position='end'>
                      <IconButton
                        onClick={() => handleClickShowPassword(field)}
                        onMouseDown={handleMouseDownPassword}
                      >
                        {showPassword[field] ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
                {errors[field] && (
                  <FormHelperText>
                    {errors[field] === true ? `${field === 'oldPassword' ? 'Old' : field === 'newPassword' ? 'New' : 'Confirm'} password is required.` : errors[field]}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>
          ))}
          <Box sx={{ py: 2 }}>
            <Button
              type='submit'
              fullWidth
              variant='contained'
              sx={{ fontFamily: 'Lato, sans-serif', fontWeight: 700, fontSize: '14px', color: '#FFF', py: 1, background: 'linear-gradient(270.29deg, #43B0D7 0.24%, #0D2663 99.75%)', textTransform: 'none' }}
            >
              Change Password
            </Button>
          </Box>
        </form>
      </Container>
    </>
  );
}
